/*
 * File: /src/store/modules/agents.js
 * Created Date: Tuesday November 30th 2021
 * Author: Nicolas Wegener, Roy Collins
 * -----
 * Last Modified: Tuesday, 30th November 2021 5:52:55 pm
 * Modified By: Nicolas Wegener (wegenern@gmail.com)
 * -----
 * Copyright (c) 2021 AdBase, Inc.
 */

import agentService from 'src/services/agentService';

const state = {
  agents: undefined,
  agentStatuses: undefined, // All statuses
};

// In flight agents request shared by callers that load at the same time
let agentsRequest;

// Seed the shared photo cache from the agents list. The API sends the url
// for public photos, anything else is resolved through the asset endpoint.
function cacheAgentPhotos(dispatch, agents) {
  const unresolved = [];
  for (const agent of agents || []) {
    if (!agent.photo_asset_id) {
      continue;
    }
    if (agent.photo_url) {
      dispatch(
        'userPhotos/setCachedPhotoUrl',
        { assetId: agent.photo_asset_id, url: agent.photo_url },
        { root: true }
      );
    } else {
      unresolved.push(agent.photo_asset_id);
    }
  }
  if (unresolved.length) {
    dispatch('userPhotos/resolvePhotoUrls', unresolved, { root: true });
  }
}

const getters = {
  getAgent: state => id => {
    if (!state.agents || !state.agents.filter || state.agents.length === 0)
      return undefined;
    const agent = state.agents.filter(a => +a.id === +id);
    if (agent && agent.length > 0) {
      return agent[0];
    }
    return undefined;
  },
  getAgents: state  => {
    return state.agents;
  },
  getAgentStatuses: state  => {
    return state.agentStatuses;
  },
};

const actions = {
 fetchAgents({ commit, dispatch, state }) {
   if (state.agents !== undefined) {
     return Promise.resolve(state.agents);
   }
   if (!agentsRequest) {
     agentsRequest = agentService
      .getAgents(0, 1000)
      .then(response => {
        commit('setAgents', response);
        cacheAgentPhotos(dispatch, response);
        // For now just alert if we are in a breaking state.
        if (response.length === 1000) {
          console.error("We have more agents than the system can handle: 1000");
        }
        return response;
      })
      .finally(() => {
        agentsRequest = undefined;
      });
   }
   return agentsRequest;
  },
  unloadAgents({ commit }) {
    commit('setAgents', undefined);
  },
 fetchAgentStatuses({ commit }) {
   if (state.agentStatuses === undefined) {
     return agentService
      .getConfig()
      .then(response => {
        commit('setAgentStatuses', response);
        return response;
      });
   }
  },
  unloadAgentStatuses({ commit }) {
    commit('setAgentStatuses', undefined);
  },
};

const mutations = {
  setAgents(state, agents) {
    state.agents = Object.assign([], agents);
  },
  removeAgent(state, agentId) {
    state.agents = state.agents.filter(d => d.id !== agentId);
  },
  setAgentStatuses(state, statuses) {
    state.agentStatuses = Object.assign([], statuses);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
